// Full Row Detail: which categorization rule sets a detail metric uses. Dependency-free on
// purpose (types only) so both ruleInterpretation.ts and the SQL/Excel helpers can import it
// without a cycle.
//
// Server-side mirror: ReportSqlBuilder.UsesDetailRuleSetList / DetailRuleSetAlias.
import type { MetricSection } from "../types";

// SELECT alias and ExcelColumnLayout.sourceAlias of a rule set's column on a detail sheet.
// Uses the rule set's id, never its name, so renaming a rule set doesn't break the layout.
export const DETAIL_RULE_SET_ALIAS_PREFIX = "detail:ruleSet:";

export function detailRuleSetAlias(ruleSetId: string) {
    return `${DETAIL_RULE_SET_ALIAS_PREFIX}${ruleSetId}`;
}

export function isDetailRuleSetAlias(sourceAlias: string) {
    return sourceAlias.startsWith(DETAIL_RULE_SET_ALIAS_PREFIX);
}

/** True when a detail metric uses the multi-select list (new behaviour). */
export function usesDetailRuleSetList(section: MetricSection) {
    return section.displayAs === "detail" && Array.isArray(section.detailRuleSetIds);
}

/**
 * The rule sets a detail metric is categorized by, in order. For a config saved before the
 * list existed this is its single legacy rule set (if any), which is what the picker shows
 * until the user changes the selection.
 */
export function selectedDetailRuleSetIds(section: MetricSection): string[] {
    if (section.displayAs !== "detail") return [];
    if (Array.isArray(section.detailRuleSetIds)) return section.detailRuleSetIds;
    return section.breakdownSource === "ruleSet" && section.ruleSetId ? [section.ruleSetId] : [];
}

/**
 * Writes a new selection. Switches the metric to the list for good and clears the legacy
 * single-rule-set fields, so there is only ever one source of truth for a detail metric.
 */
export function withDetailRuleSetIds(section: MetricSection, ruleSetIds: string[]): MetricSection {
    const unique = ruleSetIds.filter((id, index) => id && ruleSetIds.indexOf(id) === index);
    // Keep aliases only for rule sets that are still selected.
    const aliases = Object.fromEntries(Object.entries(section.detailRuleSetAliases || {}).filter(([id]) => unique.includes(id)));
    return { ...section, detailRuleSetIds: unique, detailRuleSetAliases: aliases, breakdownSource: "column", ruleSetId: "" };
}

/** The user's alias for one selected rule set's column ("" when none was typed). */
export function detailRuleSetAliasText(section: MetricSection, ruleSetId: string) {
    return (section.detailRuleSetAliases || {})[ruleSetId] || "";
}

/** Column name shown in the query and used as the default sheet header. */
export function detailRuleSetDisplayName(section: MetricSection, ruleSetId: string, ruleSetName?: string) {
    return detailRuleSetAliasText(section, ruleSetId).trim() || (ruleSetName || "").trim() || "Category";
}

export function withDetailRuleSetAlias(section: MetricSection, ruleSetId: string, alias: string): MetricSection {
    return { ...section, detailRuleSetAliases: { ...(section.detailRuleSetAliases || {}), [ruleSetId]: alias } };
}

// SQL Server identifiers are limited to 128 characters.
export const DETAIL_RULE_SET_ALIAS_MAX_LENGTH = 128;
